import { Alert, AlertDescription, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { IEtagged } from '@/domains/api';
import type { IRefreshSchedule, IRefreshSlot } from '../../api/brokercheck-refresh-contracts';
import { brokerCheckRefreshApi } from '../../api/brokercheck-refresh-api';
import { refreshClock, type IRefreshClock } from '../../brokercheck-refresh';
import {
  AdminPage,
  LoadError,
  REFRESH_TABS,
  Section,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { NoteLine, sentence, unansweredNote, type ISaidNote } from '../person-hub/said';
import { ScheduleForm } from './schedule-form';

/**
 * The BrokerCheck refresh's Schedule tab, `/team/setup/brokercheck-refresh` (SR-222, F10's R1–R5,
 * R11): survey-api's one schedule row, as a form saved under `If-Match`, and the next slot it gives.
 * Admin alone — every route of the refresh is `compliance.configure` — and a Setup area, its root
 * this tab, so the rail's entry stays lit on the Runs tab and on a run.
 *
 * **The next slot is survey-api's, said as saved** (R2). It is the read's — or the save's —
 * `nextSlot`, never this app's arithmetic: a second evaluator of `BrokerCheckRefreshSlots` would
 * agree with it until a clock change. An edit not yet saved does not move it, and the page says so
 * while the form holds one. survey-api computes it whether the schedule is on or off, so it is
 * drawn either way, under the off sentence while `enabled` is false.
 *
 * **Every instant is in the schedule's own zone, named once** (Q2, `refreshClock`) — the zone as
 * saved, not as typed in the form; a zone the browser cannot resolve is drawn in UTC, said.
 *
 * **The form takes each new revision in place** (`ScheduleForm`): a save's answer is adopted with
 * its `ETag`, a Reload's read lands a new one, and either starts the form from the row as saved;
 * a read that brings the same revision — a save whose answer never arrived and did not land —
 * leaves the typing where it was. What the page said of the last save goes as the next one starts.
 * Nothing here polls (R9).
 */
export function AdminRefreshSchedulePage() {
  const { data, error, reload, adopt } = useAdminData<IEtagged<IRefreshSchedule>>((signal) =>
    brokerCheckRefreshApi.schedule(signal)
  );
  const [dirty, setDirty] = React.useState(false);
  const [note, setNote] = React.useState<Nullable<ISaidNote>>(null);

  const saved = (answer: IEtagged<IRefreshSchedule>) => {
    adopt(answer);
    const clock = refreshClock(answer.body.timeZone);
    const slot = slotText(clock, answer.body.nextSlot);
    setNote({
      tone: 'done',
      text: answer.body.enabled
        ? `Saved. The next slot is ${slot}.`
        : `Saved. The schedule is off — the next slot it would take is ${slot}.`
    });
  };

  const unanswered = async () => {
    setNote(unansweredNote(await reload(), 'schedule'));
  };

  return (
    <AdminPage title="BrokerCheck refresh" crumb="Setup" tabs={REFRESH_TABS}>
      {error && data === null ? (
        <>
          <LoadError message={error} />
          <div>
            <Button type="button" size="sm" variant="outline" onClick={() => void reload()}>
              Read again
            </Button>
          </div>
        </>
      ) : data === null ? (
        <TableSkeleton rows={4} />
      ) : (
        <ScheduleBody
          read={data}
          error={error}
          dirty={dirty}
          note={note}
          form={
            <ScheduleForm
              schedule={data.body}
              etag={data.etag}
              onDirty={setDirty}
              onSaving={() => setNote(null)}
              onSaved={saved}
              onUnanswered={() => void unanswered()}
              onReload={async () => {
                const read = await reload();
                if (read) setNote(null);
                return read;
              }}
            />
          }
        />
      )}
    </AdminPage>
  );
}

function slotText(clock: IRefreshClock, slot: IRefreshSlot): string {
  return clock.window(slot.startsAt, slot.endsAt);
}

/** The zone every time on a refresh screen is in, said once (Q2). */
export function ZoneLine({ clock, stored }: { clock: IRefreshClock; stored: string }) {
  return (
    <p className="text-[0.8125rem] text-muted-foreground" data-time-zone={clock.zone}>
      {clock.fallback
        ? `Times are in UTC — this browser does not know the schedule’s time zone, ${stored}.`
        : `Times are in ${clock.zone}.`}
    </p>
  );
}

function ScheduleBody({
  read,
  error,
  dirty,
  note,
  form
}: {
  read: IEtagged<IRefreshSchedule>;
  error: Nullable<string>;
  dirty: boolean;
  note: Nullable<ISaidNote>;
  form: React.ReactNode;
}) {
  const schedule = read.body;
  const clock = refreshClock(schedule.timeZone);
  const slot = slotText(clock, schedule.nextSlot);

  return (
    <>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            Could not refresh — showing the schedule as last read. {sentence(error)}
          </AlertDescription>
        </Alert>
      )}
      <ZoneLine clock={clock} stored={schedule.timeZone} />

      <Section title="Next slot">
        <div className="grid gap-2 px-6 pb-6 text-[0.8125rem]">
          <p>
            {schedule.enabled
              ? 'Next slot, as saved: '
              : 'The schedule is off — nothing will run. The next slot it would take: '}
            <span data-next-slot className="font-medium">
              {slot}
            </span>
          </p>
          {dirty && (
            <p className="text-muted-foreground">
              Save to see the next slot this gives — the form holds changes not saved.
            </p>
          )}
        </div>
      </Section>

      {note && <NoteLine note={note} />}

      <Section
        title="Schedule"
        description={
          schedule.updatedBy === null
            ? `As seeded, ${clock.instant(schedule.updatedAt)}.`
            : `Last changed ${clock.instant(schedule.updatedAt)} by ${schedule.updatedBy.email}.`
        }
      >
        <div className="grid gap-5 px-6 pb-6">
          <p className="max-w-3xl text-[0.8125rem] text-muted-foreground">
            A scheduled run asks BrokerCheck about every CRD the platform holds, and IAPD about
            every person’s, inside its slot’s window: at the window’s close it pauses, and at the
            next slot it resumes over what it has not reached — while the schedule is enabled. A
            disabled schedule starts and resumes nothing, and stops nothing: only Cancel, on the
            Runs tab, stops a run. A run started with Run now has no window and goes on until it is
            done. A change saved here applies from the next slot.
          </p>
          {form}
        </div>
      </Section>
    </>
  );
}
